import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { adminAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole, isOrganizerRole } from '../../utils/roles';

const TICKET_STATUSES = ['', 'CONFIRMED', 'USED', 'REFUNDED', 'CANCELLED', 'PENDING'];

const STATUS_BADGE = {
  CONFIRMED: 'bg-emerald-100 text-emerald-800',
  USED: 'bg-blue-100 text-blue-800',
  REFUNDED: 'bg-amber-100 text-amber-900',
  CANCELLED: 'bg-gray-100 text-gray-700',
  PENDING: 'bg-yellow-100 text-yellow-800'
};

const PAY_BADGE = {
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  PROCESSING: 'bg-blue-100 text-blue-800',
  FAILED: 'bg-red-100 text-red-800',
  REFUNDED: 'bg-amber-100 text-amber-900',
  CANCELLED: 'bg-gray-100 text-gray-700'
};

const formatMoney = (n) => `KES ${Number(n || 0).toLocaleString('en-KE')}`;

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const SalesPage = () => {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [events, setEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [closedTickets, setClosedTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [complimentary, setComplimentary] = useState(null);
  const [purchased, setPurchased] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [compOpen, setCompOpen] = useState(false);
  const [compSaving, setCompSaving] = useState(false);
  const [compGuest, setCompGuest] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });
  const canIssueComplimentary = isAdmin || isOrganizerRole(user?.role);

  const [eventId, setEventId] = useState(searchParams.get('eventId') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [ticketType, setTicketType] = useState(searchParams.get('ticketType') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [dateFrom, setDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('dateTo') || '');
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalTickets: 0
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const res = await adminAPI.getEvents({ limit: 100, includeDisabled: 'true' });
        const list = res?.data?.events || [];
        setEvents(list);
        if (!isAdmin && !eventId && list.length === 1) {
          setEventId(list[0].id);
        }
      } catch (err) {
        toast.error(err.message || 'Failed to load events');
      }
    };
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const syncParams = useCallback(() => {
    const next = new URLSearchParams();
    if (eventId) next.set('eventId', eventId);
    if (status) next.set('status', status);
    if (ticketType) next.set('ticketType', ticketType);
    if (debouncedSearch) next.set('search', debouncedSearch);
    if (dateFrom) next.set('dateFrom', dateFrom);
    if (dateTo) next.set('dateTo', dateTo);
    if (page > 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [eventId, status, ticketType, debouncedSearch, dateFrom, dateTo, page, setSearchParams]);

  useEffect(() => {
    syncParams();
  }, [syncParams]);

  const fetchTickets = useCallback(async () => {
    if (!isAdmin && !eventId) {
      setTickets([]);
      setClosedTickets([]);
      setSummary(null);
      setComplimentary(null);
      setPurchased(null);
      setPackages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page,
        limit: 50
      };
      if (eventId) params.eventId = eventId;
      if (status) params.status = status;
      if (ticketType) params.ticketType = ticketType;
      if (debouncedSearch) params.search = debouncedSearch;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const closedParams = eventId && !status
        ? {
            eventId,
            closedOnly: 'true',
            limit: 100,
            ...(ticketType ? { ticketType } : {}),
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(dateFrom ? { dateFrom } : {}),
            ...(dateTo ? { dateTo } : {})
          }
        : null;

      const [ticketsRes, summaryRes, closedRes] = await Promise.all([
        adminAPI.getTickets(params),
        eventId ? adminAPI.getSalesSummary(eventId) : Promise.resolve(null),
        closedParams ? adminAPI.getTickets(closedParams) : Promise.resolve(null)
      ]);

      if (ticketsRes?.success) {
        setTickets(ticketsRes.data.tickets || []);
        setPagination(ticketsRes.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalTickets: 0
        });
      }

      if (closedRes?.success) {
        setClosedTickets(closedRes.data.tickets || []);
      } else {
        setClosedTickets([]);
      }

      if (summaryRes?.success) {
        setSummary(summaryRes.data.summary || null);
        setComplimentary(summaryRes.data.complimentary || null);
        setPurchased(summaryRes.data.purchased || null);
        setPackages(summaryRes.data.packages || []);
      } else if (!eventId) {
        setSummary(null);
        setComplimentary(null);
        setPurchased(null);
        setPackages([]);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [eventId, status, ticketType, debouncedSearch, dateFrom, dateTo, page, isAdmin]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const packageOptions = useMemo(() => {
    if (packages.length) {
      return packages.map((p) => ({ value: p.id, label: p.name }));
    }
    const map = new Map();
    tickets.forEach((t) => {
      if (t.ticketType) {
        map.set(t.ticketType, t.packageName || t.ticketType);
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [packages, tickets]);

  const selectedEventTitle = events.find((e) => e.id === eventId)?.title;

  const handleCheckIn = async (ticket, undo = false) => {
    const confirm = await Swal.fire({
      icon: undo ? 'question' : 'info',
      title: undo ? 'Undo check-in?' : 'Mark checked in?',
      text: undo
        ? `Return ${ticket.ticketNumber} to CONFIRMED.`
        : `Mark ${ticket.ticketNumber} as USED.`,
      showCancelButton: true,
      confirmButtonColor: '#ea580c',
      confirmButtonText: undo ? 'Undo' : 'Check in'
    });
    if (!confirm.isConfirmed) return;

    setActionId(ticket.id);
    const prev = tickets;
    setTickets((list) =>
      list.map((t) =>
        t.id === ticket.id
          ? {
              ...t,
              status: undo ? 'CONFIRMED' : 'USED',
              usedAt: undo ? null : new Date().toISOString()
            }
          : t
      )
    );

    try {
      const res = await adminAPI.markTicketUsed(ticket.id, { undo });
      if (res?.success) {
        toast.success(res.message || (undo ? 'Check-in undone' : 'Checked in'));
        if (eventId) {
          const summaryRes = await adminAPI.getSalesSummary(eventId);
          if (summaryRes?.success) setSummary(summaryRes.data.summary);
        }
      }
    } catch (err) {
      setTickets(prev);
      toast.error(err.message || 'Check-in failed');
    } finally {
      setActionId(null);
    }
  };

  const handleRefund = async (ticket) => {
    const isComp = ticket.isComplimentary || Number(ticket.price) === 0;
    const { value: reason, isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: isComp ? 'Void complimentary ticket?' : 'Refund ticket?',
      html: isComp
        ? `<p class="text-sm text-left mb-2">Voids <strong>${ticket.ticketNumber}</strong>. There was no payment — this only removes the free pass from the active list (gate code stops working).</p>`
        : `<p class="text-sm text-left mb-2">Marks <strong>${ticket.ticketNumber}</strong> as refunded in the system. M-Pesa money reversal is manual/ops — this does not auto-reverse funds.</p>`,
      input: 'text',
      inputPlaceholder: isComp ? 'Void reason (optional)' : 'Refund reason (optional)',
      showCancelButton: true,
      confirmButtonColor: '#c2410c',
      confirmButtonText: isComp ? 'Void pass' : 'Record refund'
    });
    if (!isConfirmed) return;

    setActionId(ticket.id);
    try {
      const res = await adminAPI.refundTicket(ticket.id, { reason: reason || undefined });
      if (res?.success) {
        toast.success(res.message || (isComp ? 'Complimentary ticket voided' : 'Ticket refunded'));
        await fetchTickets();
      }
    } catch (err) {
      toast.error(err.message || (isComp ? 'Void failed' : 'Refund failed'));
    } finally {
      setActionId(null);
    }
  };

  const handleEmail = async (ticket) => {
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Email buyer?',
      html: `Send ticket PDF + QR to<br/><strong>${ticket.buyerEmail}</strong>?`,
      showCancelButton: true,
      confirmButtonColor: '#ea580c',
      confirmButtonText: 'Send email',
      cancelButtonText: 'Cancel'
    });
    if (!confirm.isConfirmed) return;

    setActionId(ticket.id);
    Swal.fire({
      title: 'Sending email…',
      html: `Preparing PDF & QR, then sending to<br/><strong>${ticket.buyerEmail}</strong><br/><span class="text-sm text-gray-500">This can take up to a minute.</span>`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const res = await adminAPI.emailTicket(ticket.id);
      await Swal.fire({
        icon: 'success',
        title: 'Email sent',
        html: res?.message || `Ticket email sent to <strong>${ticket.buyerEmail}</strong>`,
        confirmButtonColor: '#ea580c',
        confirmButtonText: 'Done'
      });
      toast.success(res?.message || 'Email sent');
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Email failed',
        text: err.message || 'Failed to send email. Check SMTP settings and try again.',
        confirmButtonColor: '#ea580c',
        confirmButtonText: 'Close'
      });
      toast.error(err.message || 'Failed to send email');
    } finally {
      setActionId(null);
    }
  };

  const handleExport = async (type) => {
    if (!eventId) {
      toast.error('Select an event to export');
      return;
    }
    setExporting(type);
    try {
      if (type === 'xlsx') await adminAPI.exportSalesExcel(eventId);
      else await adminAPI.exportSalesPdf(eventId);
      toast.success(type === 'xlsx' ? 'Excel downloaded' : 'PDF downloaded');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const handleIssueComplimentary = async (e) => {
    e.preventDefault();
    if (!eventId) {
      toast.error('Select an event first');
      return;
    }
    const guest = {
      firstName: compGuest.firstName.trim(),
      lastName: compGuest.lastName.trim(),
      email: compGuest.email.trim(),
      phoneNumber: compGuest.phoneNumber.trim()
    };
    if (!guest.firstName || !guest.lastName || !guest.email || !guest.phoneNumber) {
      toast.error('Fill all guest details');
      return;
    }

    setCompSaving(true);
    try {
      const res = await adminAPI.issueComplimentaryTickets(eventId, [guest]);
      if (res?.success) {
        toast.success(res.message || 'Complimentary ticket emailed');
        setCompGuest({ firstName: '', lastName: '', email: '', phoneNumber: '' });
        setCompOpen(false);
        await fetchTickets();
      }
    } catch (err) {
      toast.error(err.message || 'Could not issue complimentary ticket');
    } finally {
      setCompSaving(false);
    }
  };

  const resetFilters = () => {
    setStatus('');
    setTicketType('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
    if (isAdmin) setEventId('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="lg:pl-72">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} title="Sales / Tickets" />

        <main className="p-4 sm:p-6 space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Event sales</h1>
              <p className="text-sm text-gray-600 mt-1">
                Ticket + payment detail, check-in, refunds, and exports
                {selectedEventTitle ? ` · ${selectedEventTitle}` : ''}.{' '}
                <Link to="/admin/follow-ups" className="text-orange-700 font-medium hover:underline">
                  Failed checkouts → Follow-ups
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {canIssueComplimentary && complimentary && (
                <button
                  type="button"
                  disabled={!eventId || (complimentary.remaining != null && complimentary.remaining <= 0)}
                  onClick={() => setCompOpen(true)}
                  className="px-3 py-2 text-sm rounded-lg bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                >
                  Issue complimentary
                  {complimentary.remaining != null ? ` (${complimentary.remaining} left)` : ''}
                </button>
              )}
              <button
                type="button"
                disabled={!eventId || exporting === 'xlsx'}
                onClick={() => handleExport('xlsx')}
                className="px-3 py-2 text-sm rounded-lg bg-white border border-gray-300 text-gray-800 hover:bg-orange-50 disabled:opacity-50"
              >
                {exporting === 'xlsx' ? 'Exporting…' : 'Export Excel'}
              </button>
              <button
                type="button"
                disabled={!eventId || exporting === 'pdf'}
                onClick={() => handleExport('pdf')}
                className="px-3 py-2 text-sm rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
              >
                {exporting === 'pdf' ? 'Exporting…' : 'Export PDF report'}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Event</label>
                <select
                  value={eventId}
                  onChange={(e) => {
                    setEventId(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {isAdmin && <option value="">All events</option>}
                  {!isAdmin && <option value="">Select event…</option>}
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Ticket status</label>
                <select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {TICKET_STATUSES.map((s) => (
                    <option key={s || 'all'} value={s}>{s || 'All statuses'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Package</label>
                <select
                  value={ticketType}
                  onChange={(e) => {
                    setTicketType(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">All packages</option>
                  {packageOptions.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Search</label>
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Name, email, phone, ticket #, bill ref…"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-orange-700"
                >
                  Reset filters
                </button>
              </div>
            </div>
            {!isAdmin && !eventId && (
              <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                Select an event to view sales and tickets.
              </p>
            )}
          </div>

          {/* Summary cards */}
          {summary && eventId && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  {
                    label: 'Purchased',
                    value: purchased?.max != null
                      ? `${purchased.issued} / ${purchased.max}`
                      : String(purchased?.issued ?? summary.purchasedIssued ?? 0),
                    hint: purchased?.remaining != null ? `${purchased.remaining} buyable left` : 'Paid tickets',
                    tone: 'orange'
                  },
                  {
                    label: 'Complimentary',
                    value: complimentary?.max != null
                      ? `${complimentary.issued} / ${complimentary.max}`
                      : String(complimentary?.issued ?? summary.complimentaryIssued ?? 0),
                    hint: complimentary?.remaining != null ? `${complimentary.remaining} left` : 'Organiser passes',
                    tone: 'emerald'
                  },
                  {
                    label: 'Gross revenue',
                    value: formatMoney(summary.grossRevenue),
                    hint: 'Paid tickets only',
                    tone: 'orange'
                  },
                  {
                    label: 'Checked in',
                    value: String(summary.usedCount),
                    hint: summary.refundedCount ? `${summary.refundedCount} refunded` : 'All ticket types',
                    tone: 'orange'
                  }
                ].map((card) => (
                  <div
                    key={card.label}
                    className={
                      card.tone === 'emerald'
                        ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 rounded-xl p-4'
                        : 'bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 rounded-xl p-4'
                    }
                  >
                    <p className={`text-xs font-semibold uppercase tracking-wide ${card.tone === 'emerald' ? 'text-emerald-800/80' : 'text-orange-700/80'}`}>
                      {card.label}
                    </p>
                    <p className="mt-1 text-xl font-bold text-gray-900 tabular-nums">{card.value}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{card.hint}</p>
                  </div>
                ))}
              </div>
              {complimentary && canIssueComplimentary && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-emerald-900">
                    Complimentary pool: <span className="font-bold tabular-nums">{complimentary.issued}{complimentary.max != null ? ` / ${complimentary.max}` : ''}</span>
                    {complimentary.remaining != null ? ` · ${complimentary.remaining} left to issue` : ''}
                  </p>
                  <button
                    type="button"
                    disabled={complimentary.remaining != null && complimentary.remaining <= 0}
                    onClick={() => setCompOpen(true)}
                    className="px-3 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Issue complimentary
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Table / cards */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {pagination.totalTickets} ticket{pagination.totalTickets !== 1 ? 's' : ''}
              </p>
              <Link to="/admin/events" className="text-sm text-orange-700 hover:underline">
                Manage events
              </Link>
            </div>

            {loading ? (
              <div className="p-12 text-center text-gray-500">Loading tickets…</div>
            ) : tickets.length === 0 ? (
              <div className="p-12 text-center text-gray-500">No tickets match these filters.</div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3">Ticket</th>
                        <th className="px-4 py-3">Buyer</th>
                        <th className="px-4 py-3">Payment</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Purchased</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {tickets.map((ticket) => (
                        <tr key={ticket.id} className="hover:bg-orange-50/40">
                          <td className="px-4 py-3 align-top">
                            <div className="font-semibold text-gray-900">{ticket.ticketNumber}</div>
                            <div className="text-xs font-mono font-semibold text-gray-800 mt-0.5">
                              Gate code: {ticket.gateCode || ticket.validationCode}
                            </div>
                            <div className="text-xs text-orange-700 mt-0.5">
                              {ticket.packageName || ticket.ticketType}
                              {ticket.isComplimentary && (
                                <span className="ml-1 inline-flex px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                                  Comp
                                </span>
                              )}
                            </div>
                            {!eventId && (
                              <div className="text-xs text-gray-500 mt-1">{ticket.event?.title}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="font-medium text-gray-900">
                              {[ticket.buyerFirstName, ticket.buyerLastName].filter(Boolean).join(' ')}
                            </div>
                            <div className="text-xs text-gray-500">{ticket.buyerEmail}</div>
                            <div className="text-xs text-gray-500">{ticket.phoneNumber}</div>
                          </td>
                          <td className="px-4 py-3 align-top">
                            {ticket.isComplimentary || Number(ticket.price) === 0 ? (
                              <>
                                <div className="font-medium text-emerald-900">Complimentary</div>
                                <span className={`inline-flex mt-1 px-2 py-0.5 rounded text-xs font-semibold ${PAY_BADGE[ticket.payment?.status] || 'bg-gray-100 text-gray-700'}`}>
                                  {ticket.payment?.status || 'COMPLETED'}
                                </span>
                                <div className="text-xs font-semibold text-emerald-800 mt-1.5">
                                  Issued by {ticket.issuedBy?.name || 'organiser'}
                                </div>
                                {ticket.issuedBy?.email && (
                                  <div className="text-xs text-gray-500">{ticket.issuedBy.email}</div>
                                )}
                              </>
                            ) : (
                              <>
                                <div className="font-medium">{formatMoney(ticket.price)}</div>
                                <span className={`inline-flex mt-1 px-2 py-0.5 rounded text-xs font-semibold ${PAY_BADGE[ticket.payment?.status] || 'bg-gray-100 text-gray-700'}`}>
                                  {ticket.payment?.status || '—'}
                                </span>
                                {ticket.payment?.billRef && (
                                  <div className="text-xs text-gray-500 mt-1">{ticket.payment.billRef}</div>
                                )}
                                {ticket.payment?.mpesaReceiptNumber && (
                                  <div className="text-xs text-gray-500">{ticket.payment.mpesaReceiptNumber}</div>
                                )}
                                {ticket.payment?.paymentProvider && (
                                  <div className="text-xs text-gray-400">{ticket.payment.paymentProvider}</div>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${STATUS_BADGE[ticket.status] || 'bg-gray-100'}`}>
                              {ticket.status}
                            </span>
                            {ticket.usedAt && (
                              <div className="text-xs text-gray-500 mt-1">
                                Used {formatDateTime(ticket.usedAt)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top text-gray-600">
                            {formatDateTime(ticket.createdAt)}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="flex flex-wrap justify-end gap-1">
                              {ticket.pdfUrl && (
                                <a
                                  href={ticket.pdfUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-white"
                                >
                                  PDF
                                </a>
                              )}
                              {ticket.status === 'USED' ? (
                                <button
                                  type="button"
                                  disabled={actionId === ticket.id}
                                  onClick={() => handleCheckIn(ticket, true)}
                                  className="px-2 py-1 text-xs rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                                >
                                  Undo
                                </button>
                              ) : (
                                ['CONFIRMED', 'PENDING'].includes(ticket.status) && (
                                  <button
                                    type="button"
                                    disabled={actionId === ticket.id}
                                    onClick={() => handleCheckIn(ticket, false)}
                                    className="px-2 py-1 text-xs rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                  >
                                    Check-in
                                  </button>
                                )
                              )}
                              {!['REFUNDED', 'CANCELLED'].includes(ticket.status) && (
                                <button
                                  type="button"
                                  disabled={actionId === ticket.id}
                                  onClick={() => handleRefund(ticket)}
                                  className="px-2 py-1 text-xs rounded border border-amber-200 text-amber-800 hover:bg-amber-50"
                                >
                                  {ticket.isComplimentary || Number(ticket.price) === 0 ? 'Void' : 'Refund'}
                                </button>
                              )}
                              <button
                                type="button"
                                disabled={actionId === ticket.id || !ticket.buyerEmail}
                                onClick={() => handleEmail(ticket)}
                                className="px-2 py-1 text-xs rounded border border-orange-200 text-orange-800 hover:bg-orange-50 disabled:opacity-50"
                              >
                                {actionId === ticket.id ? 'Sending…' : 'Email'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-gray-100">
                  {tickets.map((ticket) => (
                    <div key={ticket.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold text-gray-900">{ticket.ticketNumber}</p>
                          <p className="text-xs text-gray-500">
                            {ticket.packageName || ticket.ticketType}
                            {ticket.isComplimentary ? ' · Comp' : ''}
                          </p>
                          <p className="text-xs font-mono font-semibold text-gray-800">
                            Gate code: {ticket.gateCode || ticket.validationCode}
                          </p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_BADGE[ticket.status] || 'bg-gray-100'}`}>
                          {ticket.status}
                        </span>
                      </div>
                      <div className="text-sm">
                        <p className="font-medium">
                          {[ticket.buyerFirstName, ticket.buyerLastName].filter(Boolean).join(' ')}
                        </p>
                        <p className="text-gray-500 text-xs">{ticket.buyerEmail}</p>
                        <p className="text-gray-500 text-xs">{ticket.phoneNumber}</p>
                      </div>
                      <div className="text-xs text-gray-600 space-y-0.5">
                        {ticket.isComplimentary || Number(ticket.price) === 0 ? (
                          <>
                            <p className="font-semibold text-emerald-800">Complimentary</p>
                            <p className="font-semibold text-emerald-900">
                              Issued by {ticket.issuedBy?.name || 'organiser'}
                            </p>
                          </>
                        ) : (
                          <>
                            <p>{formatMoney(ticket.price)} · {ticket.payment?.status || '—'}</p>
                            {ticket.payment?.billRef && <p>Bill: {ticket.payment.billRef}</p>}
                            {ticket.payment?.mpesaReceiptNumber && (
                              <p>Receipt: {ticket.payment.mpesaReceiptNumber}</p>
                            )}
                          </>
                        )}
                        <p>Bought {formatDateTime(ticket.createdAt)}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {ticket.pdfUrl && (
                          <a
                            href={ticket.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200"
                          >
                            PDF
                          </a>
                        )}
                        {ticket.status === 'USED' ? (
                          <button
                            type="button"
                            onClick={() => handleCheckIn(ticket, true)}
                            className="px-3 py-1.5 text-xs rounded-lg border border-blue-200 text-blue-700"
                          >
                            Undo check-in
                          </button>
                        ) : (
                          ['CONFIRMED', 'PENDING'].includes(ticket.status) && (
                            <button
                              type="button"
                              onClick={() => handleCheckIn(ticket, false)}
                              className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white"
                            >
                              Check-in
                            </button>
                          )
                        )}
                        {!['REFUNDED', 'CANCELLED'].includes(ticket.status) && (
                          <button
                            type="button"
                            onClick={() => handleRefund(ticket)}
                            className="px-3 py-1.5 text-xs rounded-lg border border-amber-200 text-amber-800"
                          >
                            {ticket.isComplimentary || Number(ticket.price) === 0 ? 'Void' : 'Refund'}
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={actionId === ticket.id || !ticket.buyerEmail}
                          onClick={() => handleEmail(ticket)}
                          className="px-3 py-1.5 text-xs rounded-lg border border-orange-200 text-orange-800 disabled:opacity-50"
                        >
                          {actionId === ticket.id ? 'Sending…' : 'Email'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {pagination.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="text-sm px-3 py-1.5 rounded border border-gray-200 disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-sm px-3 py-1.5 rounded border border-gray-200 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {eventId && !status && closedTickets.length > 0 && (
            <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-amber-100 bg-amber-50/70">
                <h2 className="text-sm font-bold text-amber-950">Refunded & voided</h2>
                <p className="text-xs text-amber-800 mt-0.5">
                  Removed from the active list. Paid refunds still need manual M-Pesa reversal if money was taken.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-amber-50/50 text-left text-xs uppercase tracking-wide text-amber-900/70">
                    <tr>
                      <th className="px-4 py-3">Ticket</th>
                      <th className="px-4 py-3">Buyer</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Action by</th>
                      <th className="px-4 py-3">When</th>
                      <th className="px-4 py-3 text-right">Email</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {closedTickets.map((ticket) => (
                      <tr key={ticket.id} className="hover:bg-amber-50/40">
                        <td className="px-4 py-3 align-top">
                          <div className="font-semibold text-gray-900">{ticket.ticketNumber}</div>
                          <div className="text-xs font-mono text-gray-700">
                            Gate code: {ticket.gateCode || ticket.validationCode}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {ticket.packageName || ticket.ticketType}
                            {ticket.isComplimentary ? ' · Comp' : ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="font-medium">
                            {[ticket.buyerFirstName, ticket.buyerLastName].filter(Boolean).join(' ')}
                          </div>
                          <div className="text-xs text-gray-500">{ticket.buyerEmail}</div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span className="inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-900">
                            {ticket.isComplimentary || Number(ticket.price) === 0 ? 'Voided' : 'Refunded'}
                          </span>
                          {ticket.refundReason && (
                            <div className="text-xs text-gray-500 mt-1">{ticket.refundReason}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-xs text-gray-700">
                          <div>
                            {ticket.refundedBy?.name || '—'}
                          </div>
                          {ticket.issuedBy?.name && (
                            <div className="text-emerald-700 mt-1">
                              Issued by {ticket.issuedBy.name}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-xs text-gray-600">
                          {formatDateTime(ticket.refundedAt || ticket.updatedAt)}
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <button
                            type="button"
                            disabled={actionId === ticket.id || !ticket.buyerEmail}
                            onClick={() => handleEmail(ticket)}
                            className="px-2 py-1 text-xs rounded border border-orange-200 text-orange-800 hover:bg-orange-50 disabled:opacity-50"
                          >
                            Email
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>

      {compOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-200 p-5">
            <h2 className="text-lg font-bold text-gray-900">Issue complimentary ticket</h2>
            <p className="mt-1 text-sm text-gray-600">
              Organiser-only free pass. Guest gets the ticket email with QR/PDF.
              {selectedEventTitle ? ` · ${selectedEventTitle}` : ''}
              {complimentary?.max != null
                ? ` · ${complimentary.issued} issued · ${complimentary.remaining} of ${complimentary.max} left`
                : ''}
            </p>
            <form onSubmit={handleIssueComplimentary} className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-gray-500">First name</span>
                  <input
                    required
                    value={compGuest.firstName}
                    onChange={(e) => setCompGuest((g) => ({ ...g, firstName: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-gray-500">Last name</span>
                  <input
                    required
                    value={compGuest.lastName}
                    onChange={(e) => setCompGuest((g) => ({ ...g, lastName: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <label className="block text-sm">
                <span className="text-xs font-semibold text-gray-500">Email</span>
                <input
                  required
                  type="email"
                  value={compGuest.email}
                  onChange={(e) => setCompGuest((g) => ({ ...g, email: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs font-semibold text-gray-500">Phone</span>
                <input
                  required
                  type="tel"
                  value={compGuest.phoneNumber}
                  onChange={(e) => setCompGuest((g) => ({ ...g, phoneNumber: e.target.value }))}
                  placeholder="07XX XXX XXX"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setCompOpen(false)}
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={compSaving}
                  className="flex-1 rounded-lg bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {compSaving ? 'Issuing…' : 'Issue & email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPage;
